import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	OnDestroy,
	OnInit,
	Renderer2,
	ViewChild
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import * as objectPath from 'object-path';

import {
	LayoutConfigService,
	MenuAsideService,
	MenuOptions,
	OffcanvasOptions
} from '../../../core/_base/layout';
import { HtmlClassService } from '../html-class.service';

declare const KTOffcanvas: any;
declare const KTUtil: any;

@Component({
	selector: 'kt-aside-left',
	templateUrl: './aside-left.component.html',
	styleUrls: ['./aside-left.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class AsideLeftComponent implements OnInit, AfterViewInit, OnDestroy {
	private offcanvas: any;
	private destroy$ = new Subject<void>();

	@ViewChild('asideMenuOffcanvas', { static: true })
  asideMenuOffcanvas!: ElementRef;
	@ViewChild('asideMenu', { static: true })
 asideMenu: ElementRef | undefined;

	asideLogo = '';
	asideClasses = '';
	currentRouteUrl = '';

	insideTm: any;
	outsideTm: any;

	menuList: any[] = [];

	private activePath = new Set<any>();
	private activeKeys = new Set<string>();
	private activeLeafItem: any = null;

	menuCanvasOptions: OffcanvasOptions = {
		baseClass: 'aside',
		overlay: true,
		closeBy: 'kt_aside_close_btn',
		toggleBy: {
			target: 'kt_aside_mobile_toggle',
			state: 'mobile-toggle-active'
		}
	};

	menuOptions: MenuOptions = {
		submenu: {
			desktop: {
				default: 'accordion'
			},
			tablet: 'accordion',
			mobile: 'accordion'
		},
		accordion: {
			expandAll: false
		}
	};

	constructor(
		public htmlClassService: HtmlClassService,
		public menuAsideService: MenuAsideService,
		public layoutConfigService: LayoutConfigService,
		private router: Router,
		private render: Renderer2,
		private cdr: ChangeDetectorRef
	) {}

	ngOnInit(): void {
		this.currentRouteUrl = this.normalizeUrl(this.router.url);

		this.menuAsideService.menuList$
			.pipe(takeUntil(this.destroy$))
			.subscribe(menu => {
				this.menuList = menu || [];
				this.assignMenuKeys(this.menuList);
				this.refreshActiveMenuState();

				this.cdr.markForCheck();

				setTimeout(() => {
					this.removeInactiveKtMenuClasses();
				});
			});

		this.router.events
			.pipe(
				filter(event => event instanceof NavigationEnd),
				takeUntil(this.destroy$)
			)
			.subscribe(() => {
				this.currentRouteUrl = this.normalizeUrl(this.router.url);
				this.refreshActiveMenuState();
				this.mobileMenuClose();

				this.cdr.markForCheck();

				setTimeout(() => {
					this.removeInactiveKtMenuClasses();
				});
			});

		const config = this.layoutConfigService.getConfig();

		if (objectPath.get(config, 'aside.menu.dropdown')) {
			this.render.setAttribute(this.asideMenu.nativeElement, 'data-ktmenu-dropdown', '1');
			this.render.setAttribute(
				this.asideMenu.nativeElement,
				'data-ktmenu-dropdown-timeout',
				objectPath.get(config, 'aside.menu.submenu.dropdown.hover-timeout')
			);
		}

		this.asideClasses = this.htmlClassService.getClasses('aside', true).toString();
		this.asideLogo = this.getAsideLogo();

		setTimeout(() => {
			this.offcanvas = new KTOffcanvas(
				this.asideMenuOffcanvas.nativeElement,
				this.menuCanvasOptions
			);
		});
	}

	ngAfterViewInit(): void {}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	getAsideLogo(): string {
		let result = 'logo-light.png';
		const brandSelfTheme = this.layoutConfigService.getConfig('brand.self.theme') || '';

		if (brandSelfTheme === 'light') {
			result = 'logo-dark.png';
		}

		return `./assets/media/logos/${result}`;
	}

	isMenuItemIsActive(item: any): boolean {
		return this.activePath.has(item);
	}

	isMenuItemLeafActive(item: any): boolean {
		return this.activeLeafItem === item;
	}

	isMenuRootItemIsActive(item: any): boolean {
		return this.isMenuItemIsActive(item);
	}

onParentMenuClick(item: any, event: Event): void {
	event.preventDefault();
	event.stopPropagation();

	const path = this.findPathToItem(this.menuList, item);

	if (!path.length) {
		return;
	}

	const isAlreadyOpen = this.activeKeys.has(item.__menuKey);

	if (isAlreadyOpen) {
		// Close clicked dropdown, but keep parent dropdowns open
		const parentPath = path.slice(0, -1);

		this.activePath = new Set(parentPath);
		this.activeKeys = new Set(parentPath.map(menuItem => menuItem.__menuKey));
		this.activeLeafItem = null;
	} else {
		// Open clicked dropdown
		this.activePath = new Set(path);
		this.activeKeys = new Set(path.map(menuItem => menuItem.__menuKey));
		this.activeLeafItem = null;
	}

	this.cdr.markForCheck();

	setTimeout(() => {
		this.removeInactiveKtMenuClasses();
	});
}
	onLeafMenuClick(item: any): void {
		const path = this.findPathToItem(this.menuList, item);

		this.activePath = new Set(path);
		this.activeKeys = new Set(path.map(menuItem => menuItem.__menuKey));
		this.activeLeafItem = item;

		this.cdr.markForCheck();

		setTimeout(() => {
			this.removeInactiveKtMenuClasses();
		});
	}

	mouseEnter(e: Event): void {
		if (document.body.classList.contains('aside-fixed')) {
			if (this.outsideTm) {
				clearTimeout(this.outsideTm);
				this.outsideTm = null;
			}

			this.insideTm = setTimeout(() => {
				if (
					document.body.classList.contains('aside-minimize') &&
					KTUtil.isInResponsiveRange('desktop')
				) {
					this.render.removeClass(document.body, 'aside-minimize');
					this.render.addClass(document.body, 'aside-minimize-hover');
				}
			}, 50);
		}
	}

	mouseLeave(e: Event): void {
		if (document.body.classList.contains('aside-fixed')) {
			if (this.insideTm) {
				clearTimeout(this.insideTm);
				this.insideTm = null;
			}

			this.outsideTm = setTimeout(() => {
				if (
					document.body.classList.contains('aside-minimize-hover') &&
					KTUtil.isInResponsiveRange('desktop')
				) {
					this.render.removeClass(document.body, 'aside-minimize-hover');
					this.render.addClass(document.body, 'aside-minimize');
				}
			}, 100);
		}
	}

	getItemCssClasses(item: any): string {
		let classes = 'menu-item';

		if (objectPath.get(item, 'submenu')) {
			classes += ' menu-item-submenu';
		}

		const customClass = objectPath.get(item, 'custom-class');

		if (customClass) {
			classes += ' ' + customClass;
		}

		if (objectPath.get(item, 'icon-only')) {
			classes += ' menu-item-icon-only';
		}

		return classes;
	}

	getItemAttrSubmenuToggle(item: any): string {
		if (!item.submenu) {
			return 'click';
		}

		if (objectPath.get(item, 'toggle') === 'click') {
			return 'click';
		}

		if (objectPath.get(item, 'submenu.type') === 'tabs') {
			return 'tabs';
		}

		return 'click';
	}

	mobileMenuClose(): void {
		if (KTUtil.isBreakpointDown('lg') && this.offcanvas) {
			this.offcanvas.hide();
		}
	}

	private normalizeUrl(url: string = ''): string {
		const cleanUrl = url.split(/[?#]/)[0].replace(/\/+$/, '');
		return cleanUrl || '/';
	}

	private routeMatchesPage(page: string): boolean {
		const currentUrl = this.normalizeUrl(this.currentRouteUrl);
		const pageUrl = this.normalizeUrl(page);

		if (!pageUrl || pageUrl === '/') {
			return currentUrl === pageUrl;
		}

		return currentUrl === pageUrl || currentUrl.startsWith(pageUrl + '/');
	}

	private refreshActiveMenuState(): void {
		const path = this.findBestMatchingPath(this.menuList);

		this.activePath = new Set(path);
		this.activeKeys = new Set(path.map(item => item.__menuKey));
		this.activeLeafItem = path.length ? path[path.length - 1] : null;
	}

	private findBestMatchingPath(items: any[], parentPath: any[] = []): any[] {
		let bestPath: any[] = [];
		let bestScore = -1;

		const walk = (nodes: any[], path: any[]) => {
			for (const item of nodes || []) {
				const nextPath = [...path, item];

				if (item.page && this.routeMatchesPage(item.page)) {
					const score = this.normalizeUrl(item.page).length;

					if (score > bestScore) {
						bestScore = score;
						bestPath = nextPath;
					}
				}

				if (item.submenu && item.submenu.length) {
					walk(item.submenu, nextPath);
				}
			}
		};

		walk(items, parentPath);

		return bestPath;
	}

	private assignMenuKeys(items: any[], parentKey: string = ''): void {
		for (let index = 0; index < items.length; index++) {
			const item = items[index];

			item.__menuKey = `${parentKey}${index}_${item.title || item.page || 'item'}`;

			if (item.submenu && item.submenu.length) {
				this.assignMenuKeys(item.submenu, `${item.__menuKey}_`);
			}
		}
	}

	private findPathToItem(items: any[], targetItem: any, path: any[] = []): any[] {
		for (const item of items || []) {
			const nextPath = [...path, item];

			if (item === targetItem) {
				return nextPath;
			}

			if (item.submenu && item.submenu.length) {
				const childPath = this.findPathToItem(item.submenu, targetItem, nextPath);

				if (childPath.length) {
					return childPath;
				}
			}
		}

		return [];
	}

	private removeInactiveKtMenuClasses(): void {
		if (!this.asideMenu || !this.asideMenu.nativeElement) {
			return;
		}

		const menuItems = this.asideMenu.nativeElement.querySelectorAll('li.menu-item');

		for (let index = 0; index < menuItems.length; index++) {
			const element = menuItems[index] as HTMLElement;
			const key = element.getAttribute('data-menu-key');
			const isActive = !!key && this.activeKeys.has(key);

			if (!isActive) {
				element.classList.remove(
					'menu-item-open',
					'menu-item-here',
					'menu-item-active'
				);
			}
		}
	}
}